import React, {useCallback, useMemo, useState} from 'react';
import {Button, Table} from 'antd';
import type {TableColumnsType} from 'antd';
import {useTranslation} from 'react-i18next';
import {formatEpochToDateTime} from '../../../../utils/dateTimeUtils';
import {usePodAbnormalList} from '../hooks/usePodAbnormalList';
import {PodAbnormalTaskOrder} from '../graphql/types/PodAbnormalType';
import {PodStatusTag, getPodLang, isPodUrgent, resolvePodReasonText} from '../utils/podAbnormalDisplay';
import PodAbnormalDetailDrawer from './PodAbnormalDetailDrawer';

const PodAbnormalPanel: React.FC = () => {
    const {t, i18n} = useTranslation();
    const {data, loading, refetch} = usePodAbnormalList();

    const [detailRecord, setDetailRecord] = useState<PodAbnormalTaskOrder | null>(null);
    const [detailVisible, setDetailVisible] = useState(false);

    const lang = useMemo(() => getPodLang(i18n.language), [i18n.language]);

    const handleOpenDetail = useCallback((record: PodAbnormalTaskOrder) => {
        setDetailRecord(record);
        setDetailVisible(true);
    }, []);

    const columns: TableColumnsType<PodAbnormalTaskOrder> = useMemo(
        () => [
            {
                title: t('issueParcelWorkbench.pod.table.trackingNumber'),
                key: 'trackingNumber',
                ellipsis: true,
                render: (_: unknown, record) => record.trackingNumber ?? '-',
            },
            {
                title: t('issueParcelWorkbench.pod.table.driver'),
                key: 'driver',
                ellipsis: true,
                render: (_: unknown, record) => record.deliveredDriverAccountName || '-',
            },
            {
                title: t('issueParcelWorkbench.pod.table.deliveredAt'),
                key: 'deliveredAt',
                ellipsis: true,
                render: (_: unknown, record) => formatEpochToDateTime(record.orderWithAddress.order.deliveredAt),
            },
            {
                title: t('issueParcelWorkbench.pod.table.deadline'),
                key: 'deadline',
                ellipsis: true,
                render: (_: unknown, record) => {
                    const text = formatEpochToDateTime(record.podUpdateTimeString);
                    if (text === '-') return '-';
                    return isPodUrgent(record) ? <span style={{color: '#ff4d4f'}}>{text}</span> : text;
                },
            },
            {
                title: t('issueParcelWorkbench.pod.table.reviewStatus'),
                key: 'reviewStatus',
                ellipsis: true,
                render: (_: unknown, record) => (
                    <PodStatusTag status={record.orderWithAddress.order.podReviewStatus} />
                ),
            },
            {
                title: t('issueParcelWorkbench.pod.table.disapproveReason'),
                key: 'disapproveReason',
                ellipsis: true,
                render: (_: unknown, record) =>
                    resolvePodReasonText(record.orderWithAddress.order, lang, t),
            },
            {
                title: t('issueParcelWorkbench.pod.table.action'),
                key: 'action',
                fixed: 'right',
                width: 80,
                render: (_: unknown, record) => (
                    <Button type="link" size="small" onClick={() => handleOpenDetail(record)}>
                        {t('issueParcelWorkbench.pod.table.view')}
                    </Button>
                ),
            },
        ],
        [t, lang, handleOpenDetail],
    );

    return (
        <div className="flex-1 flex flex-col">
            <style>{`
                .pod-abnormal-urgent-row td {
                    background-color: #fef2f2 !important;
                }
                .pod-abnormal-urgent-row:hover td {
                    background-color: #fee2e2 !important;
                }
            `}</style>
            <Table
                rowKey="id"
                columns={columns}
                dataSource={data}
                loading={loading}
                pagination={false}
                scroll={{x: 'max-content'}}
                size="middle"
                tableLayout="auto"
                rowClassName={(record) => (isPodUrgent(record) ? 'pod-abnormal-urgent-row' : '')}
            />

            <PodAbnormalDetailDrawer
                open={detailVisible}
                record={detailRecord}
                onClose={() => setDetailVisible(false)}
                onUpdated={refetch}
            />
        </div>
    );
};

export default PodAbnormalPanel;
